/**
 * ShellHeader — canonical authenticated top application bar.
 *
 * Shell slots are owned by contracts and the backend navigation response
 * decides which routes are visible and actionable for the active context.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  type TabProps,
} from '@mui/material';
import { useTheme } from '@/styles/use-finance-theme';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuIcon from '@mui/icons-material/Menu';
import { ROUTE_IDS } from '@finance/contracts';
import type { NavigationContractItem, RouteId } from '@finance/contracts';
import { getRouteById } from '../../routes/registry';
import { resolveIcon } from '../../runtime/icons';
import { AccountMenu } from './AccountMenu';
import { ModuleSelector } from './ModuleSelector';
import { ContextSelector } from './ContextSelector';
import { CommandPalette } from './CommandPalette';
import { resolveTKey } from '../../i18n/resolve-key';
import { ShellBrand } from './ShellBrand';
import { ShellSearchTrigger } from './ShellSearchTrigger';
import { appShadow } from '../../styles/tokens/semantic';
import { MobileNavigationDrawer } from './MobileNavigationDrawer';
import { useShellHeader } from './use-shell-header';

function isActionable(item: NavigationContractItem): boolean {
  return item.availability === 'AVAILABLE' || item.availability === 'READ_ONLY';
}

function ShellHeaderTab({
  item,
  onActivate,
  value,
  ...tabProps
}: {
  item: NavigationContractItem;
  onActivate: (routeId: RouteId) => void;
  value: RouteId;
} & Omit<
  TabProps,
  'value' | 'label' | 'icon' | 'iconPosition' | 'disabled' | 'onClick'
>) {
  const { t } = useTranslation();
  const route = getRouteById(item.routeId);
  const label = t(resolveTKey(item.labelKey));
  const Icon = resolveIcon(item.iconKey ?? route?.iconId);
  const actionable = isActionable(item);

  return (
    <Tab
      {...tabProps}
      value={value}
      aria-label={label}
      label={
        <Box component="span" sx={{ display: Icon ? { xs: 'none', md: 'inline' } : 'inline' }}>
          {label}
        </Box>
      }
      icon={Icon ? <Icon fontSize="small" /> : undefined}
      iconPosition="start"
      disabled={!actionable}
      onClick={() => actionable && onActivate(item.routeId)}
      data-testid={`shell-tab-${item.routeId}`}
      sx={{
        minWidth: { xs: 44, sm: 64, md: 88 },
        minHeight: 56,
        px: { xs: 0.75, sm: 1, md: 1.5 },
        '& .MuiTab-iconWrapper': { mb: 0, mr: { xs: 0, md: 1 } },
      }}
    />
  );
}

export function ShellHeader() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    items,
    activeRouteId,
    navigationLoading,
    navigationError,
    refetchNavigation,
    commandPaletteOpen,
    setCommandPaletteOpen,
    mobileNavOpen,
    setMobileNavOpen,
    dashboardPath,
    primaryNavigationLabel,
  } = useShellHeader();
  const controlGap = theme.financeLayout.shell.controlGap;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      data-testid="shell-header"
      sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: 1, borderColor: 'divider', boxShadow: appShadow.header }}
    >
      <Toolbar sx={{ gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
        <ShellBrand dashboardPath={dashboardPath} />
        <Box component="nav" aria-label={primaryNavigationLabel} data-testid="primary-navigation" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: controlGap, minWidth: 0 }}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label={t('shell:navigation.open')}
            onClick={() => setMobileNavOpen(true)}
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, mr: 1 }}
            data-testid="shell-mobile-nav-trigger"
          >
            <MenuIcon />
          </IconButton>
          {navigationLoading ? (
            <Skeleton variant="rounded" height={36} width="min(320px, 40vw)" data-testid="shell-navigation-loading" />
          ) : (
            <Tabs
              value={activeRouteId}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label={primaryNavigationLabel}
              textColor="primary"
              indicatorColor="primary"
              sx={{ minWidth: 0, flex: 1, display: { xs: 'none', sm: 'flex' } }}
            >
              {items.map((item) => (
                <ShellHeaderTab
                  key={item.routeId}
                  item={item}
                  value={item.routeId}
                  onActivate={(routeId) => {
                    const route = getRouteById(routeId);
                    if (route) navigate(route.path);
                  }}
                />
              ))}
            </Tabs>
          )}
          {navigationError && (
            <Tooltip title={t('common:retry')}>
              <IconButton size="small" onClick={() => void refetchNavigation()} aria-label={t('common:retry')} data-testid="shell-navigation-retry">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <ShellSearchTrigger onActivate={() => setCommandPaletteOpen(true)} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: controlGap, ml: { xs: 0, sm: 1 }, minWidth: 0 }}>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}><ModuleSelector /></Box>
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}><ContextSelector /></Box>
          <AccountMenu />
        </Box>
      </Toolbar>
      <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
      <MobileNavigationDrawer
        items={items}
        activeRouteId={activeRouteId}
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        onNavigate={(routeId) => {
          const route = getRouteById(routeId);
          if (route) {
            setMobileNavOpen(false);
            navigate(route.path);
          }
        }}
        title={primaryNavigationLabel}
      />
    </AppBar>
  );
}
